/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { BaseFeatureMock } from '../mockServer/featureMock';
import {
    CashBoxesListDto,
    CashBoxDetailsDto,
    CashBoxStatusesHistoryDto,
    CashBoxTimeDeltaHistoryDto,
    CashBoxStateHistoryDto,
    CashBoxReplacementHistoryDto,
    CashBoxAlertsHistoryDto,
    CashBoxesMap,
    CashBoxPoint,
    CashBoxDetailsForMap,
    CashboxLocationData,
    HistoryLocationsList,
    GpsTrackingLocationsList,
    CheckSumsHistoryDto,
    CashBoxAuditHistoryDto,
    CashBoxLastDocument,
    BlockCashBoxQueryParams,
    UnblockCashBoxQueryParams,
} from './cashBoxesClient.typings';
import {
    cashBoxDetailsFactory,
    cashBoxListItemFactory,
    CashBoxStatusFactory,
    CashBoxTimeDeltaFactory,
    CashBoxStateFactory,
    CashBoxAuditHistoryItemFactory,
    CashBoxReplacementFactory,
    CashBoxAlertsFactory,
    TurnoversByPeriodFactory,
    cashBoxOnMapFactory,
    cashBoxDetailsForMapFactory,
    cashBoxLocationDataFactory,
    locationsListItemFactory,
    checksumsHistoryItemFactory,
    cashBoxLastDocumentFactory,
} from './cashBoxesFactories';
import { RegistryTurnoversByPeriodDto } from '../apiCommon.typings';
import { CashBoxesListWireBody } from './cashboxesClient';

const CASH_BOXES_URL: RegExp = /\/1.0.0\/cashboxes$/;
const CASH_BOX_DETAILS_URL: RegExp = /\/cashboxes\/\d+$/;
// Case-insensitive: the real server (NestJS/Express, case-sensitive routing off by default)
// doesn't care about this path segment's casing, and the client's two "map" calls disagree on
// it (POST sends `map/cashBoxes`, GET sends `map/cashboxes/:taxNumber`) - axios-mock-adapter's
// RegExp matching, unlike the real server, *is* case-sensitive. Matching only one casing here
// silently fails closed: the GET request falls through past this route entirely and gets
// wrongly caught by CASH_BOX_DETAILS_URL below (registered later, unanchored at the start, so
// it matches "map/cashboxes/:id" too), returning a full CashBoxDetailsDto instead of the
// intended CashBoxDetailsForMap - CASH_BOX_DETAILS_URL happens to also have an `online` field,
// so the wrong payload doesn't crash the tooltip, it just serves the wrong DTO for the wrong
// endpoint.
const CASH_BOXES_FOR_MAP_URL: RegExp = /\/map\/cashBoxes/i;
const CASH_BOX_DETAILS_FOR_MAP_URL: RegExp = /map\/cashBoxes\/\d+$/i;
const CHANGE_CASH_BOX_STATUS: RegExp = /\/cashboxes\/\d+\/status/;
const BLOCK_CASH_BOX_URL: RegExp = /\/cashboxes\/\d+\/block$/;
const UNBLOCK_CASH_BOX_URL: RegExp = /\/cashboxes\/\d+\/unblock$/;
const REMOVE_BLOCK_UNBLOCK_SCHEDULE_URL: RegExp = /\/cashboxes\/\d+\/blocks$/;
const CASH_BOXES_GET_TURNOVERS: RegExp = /\/cashboxes\/\d+\/turnovers/;
const CASH_BOX_LAST_DOCUMENT_URL: RegExp = /\/cashboxes\/\d+\/lastDocument/;
const CASH_BOXES_STATUSES_HISTORY = /\/cashboxes\/\d+\/statusesHistory/;
const CASH_BOXES_TIME_DELTA_HISTORY = /\/cashboxes\/\d+\/timeDeltaHistory/;
const CASH_BOXES_STATE_HISTORY = /\/cashboxes\/\d+\/statesHistory/;
const CASH_BOXES_AUDIT_HISTORY = /\/cashboxes\/\d+\/auditHistory/;
const CASH_BOXES_REPLACEMENT_HISTORY = /\/cashboxes\/\d+\/replacements/;
const CASH_BOXES_ALERTS_HISTORY = /\/alerts\/cashboxes\/\d+/;
const CASHBOX_LOCATION_DATA = /\/cashboxes\/\d+\/gps$/;
const CASHBOX_HISTORY_LOCATIONS = /\/cashboxes\/\d+\/gpsHistory$/;
const CASHBOX_GPS_TRACKING_LOCATIONS = /\/cashboxes\/\d+\/gpsTracking$/;
const CHECKSUMS_HISTORY = /\/cashboxes\/\d+\/checksums$/;

interface CashBoxesForMapRequestBody {
    taxNumber?: string;
}

class CashBoxesFeatureMock extends BaseFeatureMock {
    // Lazily built: constructing 10k points is wasteful when a test never hits the map
    // endpoint. `onReset()` just nulls this out so the next access rebuilds it fresh.
    private cashBoxesForMapBackend: CashBoxForMapBackendStore | null = null;

    private get cashBoxesBackend(): CashBoxForMapBackendStore {
        if (!this.cashBoxesForMapBackend) {
            this.cashBoxesForMapBackend = new CashBoxForMapBackendStore({ total: 10000 });
        }
        return this.cashBoxesForMapBackend;
    }

    public readonly cashBoxesForMap = this.route<CashBoxesMap, CashBoxesForMapRequestBody>({
        method: 'post',
        url: CASH_BOXES_FOR_MAP_URL,
        defaultResponse: (config) => {
            const params = JSON.parse(config.data);
            const cashBoxesPoints = this.cashBoxesBackend.getCashBoxes();

            let data: CashBoxesMap = {
                clusters: cashBoxesClusters,
                points: cashBoxesPoints,
            };

            if (
                params.hasOwnProperty('taxNumber') &&
                data.points.find((cashBox) => cashBox.taxNumber === params.taxNumber)
            ) {
                data = {
                    clusters: [],
                    points: [data.points.find((cashBox) => cashBox.taxNumber === params.taxNumber)],
                };
            }

            if (
                params.hasOwnProperty('taxNumber') &&
                data.points.find((cashBox) => cashBox.taxNumber === params.taxNumber) === undefined
            ) {
                data = {
                    clusters: [],
                    points: [this.cashBoxesBackend.getCashBoxes()[0]],
                };
            }

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly cashBoxDetailsForMap = this.route<CashBoxDetailsForMap>({
        method: 'get',
        url: CASH_BOX_DETAILS_FOR_MAP_URL,
        defaultResponse: () => {
            const data: CashBoxDetailsForMap = cashBoxDetailsForMapFactory.item();

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly cashBoxLastDocument = this.route<CashBoxLastDocument>({
        method: 'get',
        url: CASH_BOX_LAST_DOCUMENT_URL,
        defaultResponse: () => {
            const data: CashBoxLastDocument = cashBoxLastDocumentFactory.item();

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly cashBoxesAuditHistory = this.route<CashBoxAuditHistoryDto>({
        method: 'get',
        url: CASH_BOXES_AUDIT_HISTORY,
        defaultResponse: (config) => {
            const data: CashBoxAuditHistoryDto = {
                count: 30,
                audits: CashBoxAuditHistoryItemFactory.list({ count: config.params.limit }),
            };
            return [200, data];
        },
        delayMs: 300,
    });

    public readonly cashBoxesReplacementHistory = this.route<CashBoxReplacementHistoryDto>({
        method: 'get',
        url: CASH_BOXES_REPLACEMENT_HISTORY,
        defaultResponse: () => {
            const data: CashBoxReplacementHistoryDto = {
                parents: CashBoxReplacementFactory.list({ count: 1 }),
                children: CashBoxReplacementFactory.list({ count: 3 }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly cashBoxesAlertsHistory = this.route<CashBoxAlertsHistoryDto>({
        method: 'get',
        url: CASH_BOXES_ALERTS_HISTORY,
        defaultResponse: (config) => {
            const data: CashBoxAlertsHistoryDto = {
                count: 25,
                alerts: CashBoxAlertsFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly cashBoxesStatusesHistory = this.route<CashBoxStatusesHistoryDto>({
        method: 'get',
        url: CASH_BOXES_STATUSES_HISTORY,
        defaultResponse: (config) => {
            const data: CashBoxStatusesHistoryDto = {
                count: 30,
                statuses: CashBoxStatusFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly cashBoxesTimeDeltaHistory = this.route<CashBoxTimeDeltaHistoryDto>({
        method: 'get',
        url: CASH_BOXES_TIME_DELTA_HISTORY,
        defaultResponse: (config) => {
            const data: CashBoxTimeDeltaHistoryDto = {
                count: 40,
                timeDeltas: CashBoxTimeDeltaFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly cashBoxesStateHistory = this.route<CashBoxStateHistoryDto>({
        method: 'get',
        url: CASH_BOXES_STATE_HISTORY,
        defaultResponse: (config) => {
            const data: CashBoxStateHistoryDto = {
                count: 50,
                states: CashBoxStateFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly cashBoxesTurnoversByPeriod = this.route<RegistryTurnoversByPeriodDto[]>({
        method: 'get',
        url: CASH_BOXES_GET_TURNOVERS,
        defaultResponse: () => {
            const data: RegistryTurnoversByPeriodDto[] = TurnoversByPeriodFactory.list({ count: 12 });
            TurnoversByPeriodFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 1500,
    });

    public readonly listCashBoxes = this.route<CashBoxesListDto, CashBoxesListWireBody>({
        method: 'post',
        url: CASH_BOXES_URL,
        defaultResponse: (config) => {
            const body = JSON.parse(config.data);
            const data: CashBoxesListDto = {
                afterKeyTaxNumber: '234318932',
                cashBoxes: cashBoxListItemFactory.list({ count: body.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly cashBoxDetails = this.route<CashBoxDetailsDto>({
        method: 'get',
        url: CASH_BOX_DETAILS_URL,
        defaultResponse: () => {
            const data: CashBoxDetailsDto = cashBoxDetailsFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly changeCashBoxStatus = this.route<{}>({
        method: 'post',
        url: CHANGE_CASH_BOX_STATUS,
        defaultResponse: () => [200, {}],
        delayMs: 300,
    });

    public readonly blockCashBox = this.route<{}, BlockCashBoxQueryParams>({
        method: 'post',
        url: BLOCK_CASH_BOX_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 100,
    });

    public readonly unblockCashBox = this.route<{}, UnblockCashBoxQueryParams>({
        method: 'post',
        url: UNBLOCK_CASH_BOX_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 100,
    });

    public readonly removeBlockUnblockSchedule = this.route<{}>({
        method: 'delete',
        url: REMOVE_BLOCK_UNBLOCK_SCHEDULE_URL,
        defaultResponse: () => [200, {}],
        delayMs: () => Math.random() * 100,
    });

    public readonly cashboxLocationData = this.route<CashboxLocationData>({
        method: 'get',
        url: CASHBOX_LOCATION_DATA,
        defaultResponse: () => {
            const data: CashboxLocationData = cashBoxLocationDataFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly cashboxHistoryLocations = this.route<HistoryLocationsList>({
        method: 'get',
        url: CASHBOX_HISTORY_LOCATIONS,
        defaultResponse: (config) => {
            const data: HistoryLocationsList = {
                count: 30,
                items: locationsListItemFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly cashboxGpsTrackingLocations = this.route<GpsTrackingLocationsList>({
        method: 'get',
        url: CASHBOX_GPS_TRACKING_LOCATIONS,
        defaultResponse: () => {
            const data: GpsTrackingLocationsList = {
                accuracy: 1000,
                items: locationsListItemFactory.list({ count: 50 }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly checksumsHistory = this.route<CheckSumsHistoryDto>({
        method: 'get',
        url: CHECKSUMS_HISTORY,
        defaultResponse: (config) => {
            const data: CheckSumsHistoryDto = {
                count: config.params.limit * 3,
                items: checksumsHistoryItemFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    protected onReset(): void {
        cashBoxDetailsFactory.resetCount();
        cashBoxListItemFactory.resetCount();
        CashBoxStatusFactory.resetCount();
        CashBoxTimeDeltaFactory.resetCount();
        CashBoxStateFactory.resetCount();
        CashBoxAuditHistoryItemFactory.resetCount();
        CashBoxReplacementFactory.resetCount();
        CashBoxAlertsFactory.resetCount();
        cashBoxOnMapFactory.resetCount();
        cashBoxDetailsForMapFactory.resetCount();
        cashBoxLocationDataFactory.resetCount();
        locationsListItemFactory.resetCount();
        checksumsHistoryItemFactory.resetCount();
        cashBoxLastDocumentFactory.resetCount();
        this.cashBoxesForMapBackend = null;
    }
}

export const cashBoxesMock = new CashBoxesFeatureMock();

/** Backward-compat shim - dev mock mode (mockAdapter.ts) keeps calling this untouched. */
export function addCashBoxesMockApi(mockAdapter: MockAdapter): void {
    cashBoxesMock.register(mockAdapter, { delay: true });
}

class CashBoxForMapBackendStore {
    private cashBoxes: CashBoxPoint[] = [];

    constructor(options: { total: number }) {
        for (let index = 0; index < options.total; index++) {
            const southWest = { lat: 40.384212768155045, lon: 49.83140945434571 };
            const northEast = { lat: 40.43427422075976, lon: 49.902734756469734 };

            const lonSpan = northEast.lon - southWest.lon;
            const latSpan = northEast.lat - southWest.lat;

            this.createCashBox(southWest.lat + latSpan * Math.random(), southWest.lon + lonSpan * Math.random());
        }
    }

    public getCashBoxes() {
        return this.cashBoxes;
    }

    private createCashBox(lat: number, lon: number): void {
        const cashBox = cashBoxOnMapFactory.item();

        cashBox.lat = lat;
        cashBox.lon = lon;

        this.cashBoxes.push(cashBox);
    }
}

const cashBoxesClusters = [
    {
        count: 2000,
        geoHash: 'tp5myn',
    },
    {
        count: 1400,
        geoHash: 'tp5myx',
    },
    {
        count: 1200,
        geoHash: 'tp5myg',
    },
    {
        count: 999,
        geoHash: 'tp6myx',
    },
    {
        count: 1500,
        geoHash: 'tp9myx',
    },
];
